import { useCallback, useEffect, useRef, useState } from "react";
import {
  DOCTOR_NOTICE_BODY_MAX,
  finalizeMeetingSummary,
  MEETING_SUMMARY_FIELD_LABEL,
  MEETING_SUMMARY_PROPOSAL_COPY,
  type MeetingSummaryView,
} from "@care-by-ella/contracts";
import {
  fetchMeetingSummary,
  publishMeetingSummary,
  saveMeetingSummary,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { useReadOnly } from "@/components/SupportView";
import {
  meetingSummaryAfterEdit,
  meetingSummaryApplied,
  meetingSummaryClaimErrors,
  meetingSummaryKept,
  meetingSummaryShown,
  type MeetingSummaryLocal,
  type MeetingSummaryShown,
} from "@/lib/meeting-summary";

// TES-1289 — "Sammanfattning efter vårdmötet": the server's draft in step 4
// "Recept och patienttexter". The server creates it when the session ends or the
// meeting is documented; here the doctor edits it, and her text is saved as
// she writes. A source change after her edit is only offered ("Granska
// föreslagen uppdatering"), never applied without her.
//
// `prefill` is computed by the workspace from the choices in the guided
// steps, which are not saved until the publish; the server computes the same
// text from what it has stored (contracts meeting-summary.ts).
export function useMeetingSummary({
  patientId,
  meetingId,
  prefillFor,
  enabled,
  onUnauthorized,
}: {
  patientId: string;
  meetingId: string;
  // The prefill, given whether the server records the decision as taken
  // with the meeting (the approved variant).
  prefillFor: (approvedOnServer: boolean) => string;
  enabled: boolean;
  onUnauthorized: () => void;
}) {
  // An admin in a support view reads; she never saves or publishes.
  const readOnly = useReadOnly();
  const [server, setServer] = useState<MeetingSummaryView | null>(null);
  const [local, setLocal] = useState<MeetingSummaryLocal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fail = useCallback(
    (e: unknown, message: string) => {
      if (e instanceof UnauthorizedError) onUnauthorized();
      else setError(message);
    },
    [onUnauthorized],
  );

  const reload = useCallback(() => {
    if (!enabled) return;
    fetchMeetingSummary(patientId, meetingId)
      .then((view) => {
        setServer(view);
        setLocal(null);
      })
      .catch((e) => fail(e, "Sammanfattningen kunde inte hämtas."));
  }, [enabled, patientId, meetingId, fail]);
  useEffect(reload, [reload]);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const save = useCallback(
    async (next: MeetingSummaryLocal) => {
      try {
        setServer(await saveMeetingSummary(patientId, meetingId, next));
        setError(null);
      } catch (e) {
        fail(e, "Sammanfattningen kunde inte sparas. Försök igen.");
      }
    },
    [patientId, meetingId, fail],
  );

  const prefill = prefillFor(server?.draft?.decisionApproved ?? false);
  const shown = meetingSummaryShown(server, local, prefill);

  function change(next: MeetingSummaryLocal, delay: number) {
    if (readOnly) return;
    setLocal(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void save(next), delay);
  }

  return {
    shown,
    error,
    onType: (text: string) => change(meetingSummaryAfterEdit(shown, text), 800),
    onApply: () => change(meetingSummaryApplied(shown.proposal ?? prefill), 0),
    onKeep: () => change(meetingSummaryKept(shown), 0),
    /** The finalized text that a publish sends, "" when there is none. */
    publishedText: finalizeMeetingSummary(shown.text),
    /** Publishes what the field shows. Throws on failure. */
    publish: async () => {
      if (readOnly) throw new Error("read_only");
      if (timer.current) clearTimeout(timer.current);
      const view = await publishMeetingSummary(patientId, meetingId, {
        body: shown.text,
        basisPrefill: shown.basisPrefill,
      });
      setServer(view);
      setLocal(null);
      return view;
    },
    reload,
  };
}

export function MeetingSummaryField({
  shown,
  labOrderConfirmed,
  error,
  disabled,
  onType,
  onApply,
  onKeep,
}: {
  shown: MeetingSummaryShown;
  labOrderConfirmed: boolean;
  error: string | null;
  disabled?: boolean;
  onType: (text: string) => void;
  onApply: () => void;
  onKeep: () => void;
}) {
  const claims = meetingSummaryClaimErrors(shown.text, labOrderConfirmed);
  return (
    <div className="flex flex-col gap-xs text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-xs">
        <label htmlFor="meeting-summary-text" className="font-semibold">
          {MEETING_SUMMARY_FIELD_LABEL}
        </label>
        <span
          className="text-xs text-textSecondary"
          data-testid="meeting-summary-status"
        >
          {shown.status}
        </span>
      </div>
      {shown.proposal !== null ? (
        <div
          role="status"
          className="flex flex-col gap-xs rounded-md border border-brand/30 bg-brand-accent/40 p-sm"
        >
          <p className="font-semibold">
            {MEETING_SUMMARY_PROPOSAL_COPY.review}
          </p>
          <p className="text-xs text-textSecondary">
            {MEETING_SUMMARY_PROPOSAL_COPY.explain}
          </p>
          <p className="whitespace-pre-wrap rounded-md border border-surfaceMuted bg-surface p-sm text-xs">
            {shown.proposal}
          </p>
          <div className="flex gap-sm">
            <button
              type="button"
              onClick={onApply}
              disabled={disabled}
              className="rounded-md bg-brand px-md py-xs text-xs font-semibold text-textOnBrand hover:opacity-90 disabled:opacity-50"
            >
              {MEETING_SUMMARY_PROPOSAL_COPY.apply}
            </button>
            <button
              type="button"
              onClick={onKeep}
              disabled={disabled}
              className="rounded-md border border-surfaceMuted px-md py-xs text-xs font-semibold hover:bg-brand/5 disabled:opacity-50"
            >
              {MEETING_SUMMARY_PROPOSAL_COPY.keep}
            </button>
          </div>
        </div>
      ) : null}
      <textarea
        id="meeting-summary-text"
        aria-invalid={claims.length > 0 || error ? true : undefined}
        value={shown.text}
        onChange={(e) => onType(e.target.value)}
        rows={Math.min(16, Math.max(6, shown.text.split("\n").length + 1))}
        maxLength={DOCTOR_NOTICE_BODY_MAX}
        disabled={disabled}
        className="rounded-md border border-surfaceMuted p-sm text-sm focus:border-brand focus:outline-none"
      />
      <span className="text-xs text-textSecondary">
        Förifyllt utifrån utfallet, valda kompletteringar, planerad behandling
        och bekräftad remiss. Rubriker utan innehåll tas inte med. Publiceras
        under Din vård först när du publicerar till patienten.
      </span>
      {claims.map((message) => (
        <p key={message} className="text-xs text-danger">
          {message}
        </p>
      ))}
      {error ? <p className="text-xs text-danger">{error}</p> : null}
    </div>
  );
}
